import Button from "@components/button";
import Column from "@components/column";
import Fields from "@components/fields";
import DropDown from "@components/fields/dropdown";
import Row from "@components/row";
import { Form, Formik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import Banner from "@apis/banner";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import { Brand, Store, Zone, Product } from "@apis/index";
import ButtonWrapper from "@components/wrapper/button-wrapper";
import ModuleDropdown from "@commons/modules/dropdown";
import { toast } from "react-toastify";

const BannerForm = () => {
  const { data: user } = useSelector((state: RootState) => state.user);

  const { state } = useLocation();

  const [uploadKey, setUploadKey] = useState(0);
  const { data: zones } = useSelector((state: RootState) => state.zones);
  const { items: brands } = useSelector((state: RootState) => state.brands);
  const { items: stores } = useSelector((state: RootState) => state.stores);
  const { items: products } = useSelector((state: RootState) => state.products);
  const navigate = useNavigate();
  const { id } = useParams();
  const defaultValues = {
    title: "",
    links: [],
    type: user.role === "store" ? "store" : "",
    moduleId: user.role === "store" ? user.module.id : "",
    brandId: [],
    storeId: user.role === "store" ? [user.id] : [],
    selectedStoreId: user.role === "store" ? user.id : "", // For single store selection in product banner
    productId: [],
    zoneIds: user.role === "store" ? [user.zone.id] : ([] as number[]),
    image: [] as any[],
    video: [] as any[],
    home: false,
    linkType: "link",
  };

  const [initialValues, setInitialValues] = useState(defaultValues);
  const [loading, setLoading] = useState(false);

  const fetchBanner = useCallback(async () => {
    await Banner.list({
      state: user.role + "Banners",
      params: {
        role: user.role,
        ...(user.role === "store" && { storeId: user.id }),
      },
    });
  }, [user?.role]);

  const fetchZones = useCallback((moduleId: any) => {
    Zone.list({ state: "zones", params: { moduleId: moduleId || undefined } });
  }, []);

  useEffect(() => {
    if (!state) {
      setInitialValues(defaultValues);
      fetchZones(defaultValues.moduleId);
      return;
    }

    let linkType = "link";
    if (state?.images?.some((img: any) => img.storeId)) {
      linkType = "store";
    }

    setInitialValues({
      title: state?.title || "",
      links: state?.images.map((i) => i.link) || [],
      type: state?.type || "",
      moduleId: state?.module?.id || "",
      brandId: state?.images.map((i) => i.brandId) || [],
      storeId: state?.images.map((i) => i.storeId) || [],
      selectedStoreId: state?.images?.[0]?.storeId || "",
      productId: state?.images.map((i) => i.productId) || [],
      home: state?.isHome || "",
      zoneIds: Array.isArray(state?.zones)
        ? state?.zones.map((z: any) => z?.id)
        : [],
      image: (state?.images || []).filter((i: any) => i.url && !i.videoUrl),
      video: (state?.images || []).filter((i: any) => i.videoUrl),
      linkType: linkType,
    });

    if (state?.module?.id) {
      // When editing a product banner, fetch products with storeId if available
      if (state?.type === "product" && state?.images?.[0]?.storeId) {
        fetchProducts(state.module.id, state.images[0].storeId);
      } else {
        fetchProducts(state.module.id);
      }
      fetchZones(state.module.id);
    }
  }, [state]);

  const fetchProducts = (moduleId: any, storeId?: any) => {
    console.log("fetchProducts called with:", {
      moduleId,
      storeId,
      userRole: user.role,
      userId: user.id,
    });

    const params: any = {};
    if (moduleId) params.moduleId = moduleId;

    // Filter by storeId if provided (for admin selecting store in product banner)
    if (storeId) {
      params.storeId = storeId;
    }
    // For store role, automatically filter by their store ID
    if (user.role === "store" && user.id) {
      params.storeId = user.id;
    }

    // Only fetch if we have at least a moduleId or a storeId
    if (params.moduleId || params.storeId) {
      console.log("Fetching products with params:", params);
      Product.list({ state: "products", params });
    }
  };

  const fetchStores = useCallback((moduleId: any) => {
    Store.list({
      state: "stores",
      params: { moduleId: moduleId || undefined, limit: 3000 },
    });
  }, []);

  useEffect(() => {
    fetchZones(defaultValues.moduleId);
    Brand.list({ state: "brands" });
    fetchStores(defaultValues.moduleId);
    // For store role, fetch their products on mount
    if (user.role === "store" && defaultValues.moduleId) {
      fetchProducts(defaultValues.moduleId);
    }
  }, [defaultValues.moduleId, fetchZones, fetchStores]);
  const brandoptions = brands.map((brand) => ({
    id: brand.id,
    name: brand.name,
    image: brand.icon,
  }));
  const handleSubmit = async (values: any, { resetForm }: any) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("type", values.type);
    formData.append("role", user.role);
    if (values.home) formData.append("isHome", values.home);
    if (!values.home) formData.append("moduleId", values.moduleId);

    (values.zoneIds || []).forEach((id: number) =>
      formData.append("zoneIds", id?.toString() || ""),
    );

    (values.image || []).forEach((item: any) => {
      if (item.rawFile) formData.append("images", item.rawFile);
    });

    const existingImages = (values.image || [])
      .filter((i: any) => !i.rawFile && i.id)
      .map((i: any) => i.id);

    existingImages.forEach((url: string) =>
      formData.append("existingImages", url),
    );

    // Handle videos
    (values.video || []).forEach((item: any) => {
      if (item.rawFile) formData.append("videos", item.rawFile);
    });

    const existingVideos = (values.video || [])
      .filter((i: any) => !i.rawFile && i.id)
      .map((i: any) => i.id);

    existingVideos.forEach((vid: string) =>
      formData.append("existingVideos", vid),
    );

    (values.storeId || []).forEach((id: number) =>
      formData.append("storeIds", id?.toString() || ""),
    );
    (values.brandId || []).forEach((id: number) =>
      formData.append("brandIds", id?.toString() || ""),
    );
    (values.productId || []).forEach((id: number) =>
      formData.append("productIds", id?.toString() || ""),
    );
    (values.links || []).forEach((item: any) =>
      formData.append("links", JSON.stringify(item || "")),
    );

    try {
      const promise = id
        ? Banner.update({ id, data: formData, state: user.role + "Banners" })
        : Banner.create({ data: formData, state: user.role + "Banners" });

      await toast.promise(promise, {
        pending: id ? "Updating banner..." : "Creating banner...",
        success: id
          ? "Banner updated successfully!"
          : "Banner created successfully!",
        error: "Something went wrong while saving the banner.",
      });

      // resetForm();
      setUploadKey((k) => k + 1);
      navigate(`/${user.role}/banner-settings`);
      // await fetchBanner();
    } catch (err) {
      console.error("Error creating/updating banner:", err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
      validate={(values) => {
        const errors: any = {};
        const imageCount = (values.image?.length || 0);
        const videoCount = (values.video?.length || 0);
        const totalCount = imageCount + videoCount;

        if (totalCount > 10)
          toast.error((errors.image = "At most ten media files are allowed"));

        const normalizeArray = (val: any) => {
          if (!val) return [];
          if (Array.isArray(val)) return val;
          if (typeof val === "string") return [val];
          return [];
        };

        const storeIds = normalizeArray(values.storeId);
        const brandIds = normalizeArray(values.brandId);
        const productIds = normalizeArray(values.productId);
        const links = normalizeArray(values.links);

        if (values.type === "store") {
          if (storeIds.length > 1 && storeIds.length !== totalCount) {
            errors.storeId = `Number of store IDs (${storeIds.length}) must match number of media files (${totalCount})`;
          }
        } else if (values.type === "brand") {
          if (brandIds.length > 1 && brandIds.length !== totalCount) {
            errors.brandId = `Number of brand IDs (${brandIds.length}) must match number of media files (${totalCount})`;
          }
        } else if (values.type === "product") {
          if (productIds.length > 1 && productIds.length !== totalCount) {
            errors.productId = `Number of product IDs (${productIds.length}) must match number of media files (${totalCount})`;
          }
        } else {
          if (values.linkType === "store") {
            if (storeIds.length > 1 && storeIds.length !== totalCount) {
              errors.storeId = `Number of store IDs (${storeIds.length}) must match number of media files (${totalCount})`;
            }
          } else {
            if (links.length > 1 && links.length !== totalCount) {
              errors.links = `Number of links (${links.length}) must match number of media files (${totalCount})`;
            }
          }
        }

        return errors;
      }}
    >
      {({ values, setFieldValue, resetForm, errors }) => {
        const handleReset = () => {
          resetForm();
          setUploadKey((k) => k + 1);
        };
        return (
          <Form>
            <Column>
              <Row>
                <Fields.Upload
                  key={uploadKey}
                  name="image"
                  variant="banner"
                  multiple
                  maxFiles={10}
                  label="Upload Banner Images"
                  useRawFile
                  size={100}
                  mode={!!state || state?.id ? "update" : "create"}
                  onRemoveImage={async (val) => {
                    await Banner.removeBanner({
                      state: user.role + "Banners",
                      id: val.id,
                    });
                  }}
                />

                <Fields.Upload
                  key={`video-${uploadKey}`}
                  name="video"
                  variant="banner"
                  multiple
                  maxFiles={5}
                  label="Upload Banner Videos"
                  useRawFile
                  size={100}
                  video={true}
                  mode={!!state || state?.id ? "update" : "create"}
                  onRemoveImage={async (val) => {
                    await Banner.removeBanner({
                      state: user.role + "Banners",
                      id: val.id,
                    });
                  }}
                />
              </Row>

              <Row styleRow="align-items:center">
                <Fields.Switch name="home" label="For Home" />
                <ModuleDropdown
                  styleInput="width:96%"
                  disabled={values.home || user.role === "store"}
                  onChange={(val) => {
                    setFieldValue("moduleId", val);
                    setFieldValue("productId", []);
                    setFieldValue("zoneIds", []);
                    fetchProducts(val);
                    fetchZones(val);
                    fetchStores(val);
                  }}
                />
                <DropDown
                  isMultiple
                  name="zoneIds"
                  label="Zones"
                  options={zones}
                  titleAsKey="name"
                  valueAsKey="id"
                  styleInput="width:97%"
                  styleOptionsWrapper="width:100%"
                  placeholder="Select Zones"
                  selectable
                  disabled={
                    user.role === "store" || (!values.home && !values.moduleId)
                  }
                  removeOption={false}
                  hasSearch
                />
                <DropDown
                  name="type"
                  label="Banner Type"
                  options={[
                    { title: "Store/Vendor Wise", value: "store" },
                    { title: "Brand Wise", value: "brand" },
                    { title: "Product Wise", value: "product" },
                    { title: "Header", value: "header" },
                    { title: "Checkout", value: "checkout" },
                    { title: "Sliding Banner", value: "sliding" },
                    { title: "Featured", value: "featured" },
                    { title: "Alert", value: "alert" },
                    { title: "Cart", value: "cart" },
                    { title: "Search", value: "search" },
                    { title: "Order History", value: "orderhistory" },
                  ].filter(
                    (opt) => !(user.role === "store" && opt.value === "brand"),
                  )}
                  titleAsKey="title"
                  valueAsKey="value"
                  styleInput="width:94%"
                  styleOptionsWrapper="width:100%"
                  placeholder="Select Banner Type"
                />
              </Row>

              <Row>
                <Fields.Input
                  name="title"
                  label="Banner Title"
                  placeholder="Enter Banner Title"
                />

                {/* Show store dropdown when type is store */}
                {values.type === "store" && (
                  <DropDown
                    name="storeId"
                    label="Store"
                    options={stores}
                    titleAsKey="storeName"
                    valueAsKey="id"
                    styleInput="width:97%"
                    styleOptionsWrapper="width:100%"
                    placeholder="Select Store"
                    isMultiple={true}
                    disabled={user.role === "store"}
                    removeOption={false}
                    hasSearch
                  />
                )}

                {/* Show brand dropdown when type is brand */}
                {values.type === "brand" && user.role !== "store" && (
                  <DropDown
                    name="brandId"
                    label="Brand"
                    options={brandoptions}
                    titleAsKey="name"
                    valueAsKey="id"
                    styleInput="width:97%"
                    styleOptionsWrapper="width:100%"
                    placeholder="Select Brand"
                    isMultiple={true}
                    hasSearch
                  />
                )}

                {/* Show product dropdown when type is product */}
                {values.type === "product" && (
                  <>
                    {/* For admin: Show store dropdown first to filter products */}
                    {user.role !== "store" && (
                      <DropDown
                        hasSearch
                        name="selectedStoreId"
                        label="Store (Select to filter products)"
                        options={stores}
                        titleAsKey="storeName"
                        valueAsKey="id"
                        styleInput="width:97%"
                        styleOptionsWrapper="width:100%"
                        placeholder="Select Store First"
                        isMultiple={false}
                        removeOption={false}
                        onChange={(storeId) => {
                          setFieldValue("selectedStoreId", storeId);
                          setFieldValue("storeId", storeId ? [storeId] : []);
                          setFieldValue("productId", []); // Reset products when store changes
                          if (storeId && values.moduleId) {
                            fetchProducts(values.moduleId, storeId);
                          }
                        }}
                      />
                    )}

                    <DropDown
                      name="productId"
                      label="Product"
                      options={products}
                      titleAsKey="name"
                      valueAsKey="id"
                      styleInput="width:97%"
                      styleOptionsWrapper="width:100%"
                      placeholder={
                        user.role === "store"
                          ? "Select Product"
                          : values.storeId?.length > 0
                            ? "Select Product"
                            : "Select Store First"
                      }
                      isMultiple={true}
                      disabled={
                        user.role !== "store" &&
                        (!values.storeId || values.storeId.length === 0)
                      }
                      hasSearch
                    />
                  </>
                )}

                {/* Show link type selector and corresponding field for other types */}
                {values.type &&
                  values.type !== "store" &&
                  values.type !== "brand" &&
                  values.type !== "product" && (
                    <>
                      <DropDown
                        name="linkType"
                        label="Link Type"
                        options={[
                          { title: "Redirect Link", value: "link" },
                          { title: "Store/Vendor", value: "store" },
                        ]}
                        titleAsKey="title"
                        valueAsKey="value"
                        styleInput="width:97%"
                        styleOptionsWrapper="width:100%"
                        placeholder="Select Link Type"
                        onChange={(value) => {
                          setFieldValue("linkType", value);
                          if (value === "link") {
                            setFieldValue("storeId", []);
                          } else {
                            setFieldValue("links", []);
                          }
                        }}
                        hasSearch
                      />

                      {values.linkType === "store" ? (
                        <DropDown
                          name="storeId"
                          label="Store"
                          options={stores}
                          titleAsKey="storeName"
                          valueAsKey="id"
                          styleInput="width:97%"
                          styleOptionsWrapper="width:100%"
                          placeholder="Select Store"
                          isMultiple={user.role === "store" ? false : true}
                          removeOption={false}
                          disabled={user.role === "store"}
                          hasSearch
                        />
                      ) : (
                        <Fields.TagInput
                          allowSame={true}
                          name="links"
                          label="Banner Link"
                          placeholder="Enter Redirect Link"
                          icon={false}
                        />
                      )}
                    </>
                  )}
              </Row>

              <ButtonWrapper>
                <Button
                  title={id ? "Update Banner" : "Create Banner"}
                  type="submit"
                  disabled={loading}
                />
                <Button
                  title="Reset"
                  type="reset"
                  variant="reset"
                  onClick={() => {
                    handleReset();
                    resetForm();
                  }}
                />
              </ButtonWrapper>
            </Column>
          </Form>
        );
      }}
    </Formik>
  );
};

export default BannerForm;
